import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiLogin } from "@/services/api";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await apiLogin(email, password);
      const data = res.data;
      // NaturTrak returns user data directly (name, id, etc. at top level)
      const token = data.access_token || data.token || data.auth_token || data.jwt;
      const userId = data.id;
      login({ id: userId, name: data.name, email: data.email, role: data.role }, token);
      navigate("/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Hibás e-mail cím vagy jelszó.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: "#FDF5EB", fontFamily: "Quicksand, sans-serif" }}
    >
      {/* Left panel – branding */}
      <div
        className="hidden lg:flex flex-col justify-between w-1/2 p-12"
        style={{ background: "#FBD556", borderRight: "1.5px solid #242220" }}
      >
        <div className="flex items-center gap-3">
          <img
            src="/athena_logo.svg"
            alt="Logo"
            className="w-12 h-12"
            style={{ borderRadius: "24px" }}
          />
          <span
            className="text-2xl font-bold"
            style={{ color: "#242220", fontFamily: "Quicksand, sans-serif" }}
          >
            NaturTrak Athena
          </span>
        </div>

        <div>
          <h1
            className="text-5xl font-bold leading-tight mb-6"
            style={{ color: "#242220" }}
          >
            Tanulj.
            <br />
            Fedezd fel.
            <br />
            Gyakorolj.
          </h1>
          <p className="text-lg" style={{ color: "#242220", opacity: 0.8 }}>
            A NaturTrak LMS platformon elsajátíthatod az ökoszisztéma használatát.
          </p>
        </div>

        <img
          src="https://static.prod-images.emergentagent.com/jobs/da1a3332-a31e-4aa2-9b69-b66190a6faa2/images/9b66dd5c11e68cde61575773522701f9dff0c2043ad88a85537924e25a189a18.png"
          alt="NaturTrak hero"
          className="w-full object-cover"
          style={{ borderRadius: "24px", border: "1.5px solid #242220", maxHeight: 280 }}
        />
      </div>

      {/* Right panel – form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <img
              src="/athena_logo.svg"
              alt="Logo"
              className="w-10 h-10"
              style={{ borderRadius: "18px" }}
            />
            <span className="text-xl font-bold" style={{ color: "#242220" }}>
              NaturTrak Athena LMS
            </span>
          </div>

          <div
            className="p-8"
            style={{
              background: "#fff",
              borderRadius: "24px",
              border: "1.5px solid #242220",
              boxShadow: "6px 6px 0px #242220",
            }}
          >
            <h2
              className="text-3xl font-bold mb-2"
              style={{ color: "#242220" }}
            >
              Bejelentkezés
            </h2>
            <p className="text-sm mb-8" style={{ color: "#242220", opacity: 0.6 }}>
              Lépj be a NaturTrak fiókodba
            </p>

            {error && (
              <div
                className="mb-6 px-4 py-3 text-sm font-bold"
                style={{
                  background: "#FDECEA",
                  border: "1.5px solid #D32F2F",
                  borderRadius: "16px",
                  color: "#D32F2F",
                }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: "#242220" }}
                >
                  E-mail cím
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="pelda@naturtrak.eu"
                  required
                  className="w-full px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: "#FDF5EB",
                    borderRadius: "16px",
                    border: "1.5px solid #242220",
                    fontFamily: "Quicksand, sans-serif",
                    color: "#242220",
                  }}
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: "#242220" }}
                >
                  Jelszó
                </label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full px-4 py-3 pr-12 text-sm outline-none transition-all"
                    style={{
                      background: "#FDF5EB",
                      borderRadius: "16px",
                      border: "1.5px solid #242220",
                      fontFamily: "Quicksand, sans-serif",
                      color: "#242220",
                    }}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: "#242220", opacity: 0.5 }}
                    data-testid="toggle-password-btn"
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 font-bold text-base transition-all active:translate-x-[2px] active:translate-y-[2px]"
                style={{
                  background: loading ? "#e0c74a" : "#FBD556",
                  borderRadius: "30px",
                  border: "1.5px solid #242220",
                  boxShadow: loading ? "2px 2px 0 #242220" : "4px 4px 0 #242220",
                  color: "#242220",
                  fontFamily: "Quicksand, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                data-testid="login-submit-btn"
              >
                {loading ? "Bejelentkezés..." : "Bejelentkezés"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
